import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import TwoFactorSetup from "../components/TwoFactorSetup";
import DeleteAccountModal from "../components/DeleteAccountModal";
import TalentCloudOptIn from "../components/TalentCloudOptIn";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useCopy } from "../lib/copy";
import { Mail, Send, Bell, Check, X, Palette, RotateCcw, Trash2 } from "lucide-react";

export default function Account() {
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState("");
  const [resetStatus, setResetStatus] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { mode, setMode } = useTheme();
  const { user, refresh, setUser } = useAuth();
  const t = useCopy();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/account/preferences");
        setPrefs(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    setError("");
    try {
      await api.patch("/account/preferences", patch);
      setPrefs({ ...prefs, ...patch });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestStatus("sending");
    setError("");
    try {
      await api.post("/account/test-email");
      setTestStatus("sent");
      setTimeout(() => setTestStatus(""), 4000);
    } catch (e) {
      setTestStatus("");
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const runDemoReset = async () => {
    const ok = window.confirm(
      "Wipe ALL your content (resume, analyses, skill assessments, mock interviews, journal, etc.)?\n\n" +
      "Your account, credits, and Pro status are kept. This is for re-running the demo from scratch."
    );
    if (!ok) return;
    setResetStatus("running");
    setError("");
    try {
      await api.post("/admin/demo/reset");
      setResetStatus("done");
      await refresh();
      setTimeout(() => setResetStatus(""), 4000);
    } catch (e) {
      setResetStatus("");
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <div className="rm-tag mb-3">Account</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-6">
          Tune your <span className="rm-text-orange">{t("Resmo.", "ProMo.")}</span>
        </h1>

        {error && (
          <div className="p-3 mb-6 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="account-error">
            {error}
          </div>
        )}

        {!prefs ? (
          <div className="font-mono-rm text-sm">Loading...</div>
        ) : (
          <div className="rm-card-static p-6 md:p-8 space-y-6" data-testid="account-card">
            {/* ProMo (UI mode) toggle */}
            <div className="flex items-start gap-3" data-testid="promo-toggle-section">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: mode === "promo" ? "#0F1B2D" : "#FF6A00",
                  color: "white",
                  border: "3px solid #0F1B2D",
                }}
              >
                <Palette size={20} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="rm-label mb-1">Look & feel</div>
                <div className="font-bold">
                  {mode === "promo" ? "ProMo — seasoned mode" : "Playful — full Resmo"}
                </div>
                <p className="text-sm mt-1" style={{ color: "#475569" }}>
                  {mode === "promo"
                    ? "Navy wordmark, no mascot, restrained copy. Built for senior professionals who want signal over personality."
                    : "Orange wordmark, mascot poses, monster microcopy. The original Resmo vibe — fun and memorable."}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setMode("playful")}
                  className={`rm-btn text-xs px-3 py-2 ${mode === "playful" ? "rm-btn-primary" : "rm-btn-ghost"}`}
                  data-testid="set-mode-playful"
                >
                  Playful
                </button>
                <button
                  onClick={() => setMode("promo")}
                  className={`rm-btn text-xs px-3 py-2 ${mode === "promo" ? "rm-btn-primary" : "rm-btn-ghost"}`}
                  data-testid="set-mode-promo"
                >
                  ProMo
                </button>
              </div>
            </div>

            <div className="pt-4 flex items-start gap-3" style={{ borderTop: "3px dashed #0F1B2D" }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                   style={{ background: "#FF6A00", color: "white", border: "3px solid #0F1B2D" }}>
                <Bell size={20} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="rm-label mb-1">Email reminders</div>
                <div className="font-bold">Get a friendly nudge when it's time to feed Resmo again.</div>
                <p className="text-sm mt-1" style={{ color: "#475569" }}>
                  We'll only email you if you haven't run an analysis in {prefs.reminder_days} days.
                </p>
                {!prefs.email_configured && (
                  <div className="mt-2 p-2 rounded font-mono-rm text-xs"
                       style={{ background: "#FEF3C7", border: "2px solid #F59E0B" }}>
                    Email is not configured yet. Add `RESEND_API_KEY` to backend .env to enable.
                  </div>
                )}
              </div>
              <button
                onClick={() => update({ email_reminders_enabled: !prefs.email_reminders_enabled })}
                disabled={saving || !prefs.email_configured}
                className={`rm-btn ${prefs.email_reminders_enabled ? "rm-btn-primary" : "rm-btn-ghost"} text-xs px-3 py-2`}
                data-testid="reminders-toggle"
              >
                {prefs.email_reminders_enabled ? <><Check size={14}/> On</> : <><X size={14}/> Off</>}
              </button>
            </div>

            <div className="pt-4" style={{ borderTop: "3px dashed #0F1B2D" }}>
              <label className="rm-label block mb-2">Reminder threshold (days)</label>
              <input
                type="range"
                min="1"
                max="14"
                value={prefs.reminder_days}
                onChange={(e) => update({ reminder_days: parseInt(e.target.value, 10) })}
                disabled={!prefs.email_reminders_enabled}
                className="w-full"
                data-testid="reminder-days-range"
              />
              <div className="font-mono-rm text-sm mt-1">
                {prefs.reminder_days} day{prefs.reminder_days === 1 ? "" : "s"}
              </div>
            </div>

            <div className="pt-4" style={{ borderTop: "3px dashed #0F1B2D" }}>
              <button
                onClick={sendTest}
                disabled={!prefs.email_configured || testStatus === "sending"}
                className="rm-btn rm-btn-secondary text-xs"
                data-testid="send-test-email-btn"
              >
                <Send size={14} />
                {testStatus === "sending" ? "Sending..." : testStatus === "sent" ? "Sent! ✉" : "Send test email"}
              </button>
              <div className="font-mono-rm text-xs mt-2" style={{ color: "#475569" }}>
                <Mail size={12} className="inline mr-1" /> Sends to your account email.
              </div>
            </div>

            {user?.is_admin && (
              <TwoFactorSetup
                enabled={!!user?.totp_enabled}
                onChange={() => refresh()}
              />
            )}

            {user?.is_admin && (
              <div className="pt-4" style={{ borderTop: "3px dashed #FF6A00" }} data-testid="demo-reset-section">
                <div className="rm-tag mb-2" style={{ background: "#FF6A00", borderColor: "#FF6A00", color: "white" }}>
                  Admin · Demo mode
                </div>
                <h3 className="font-display text-lg uppercase mb-2">
                  Reset for a clean demo
                </h3>
                <p className="text-sm mb-3" style={{ color: "#475569" }}>
                  Wipes your resume, analyses, skill assessments, role research,
                  mock interviews, interview banks, journal, and feedback — leaving the
                  account, credits and Pro status intact. Use this before running a
                  live walkthrough from invitation → upload → tailored resume.
                </p>
                <button
                  onClick={runDemoReset}
                  disabled={resetStatus === "running"}
                  className="rm-btn rm-btn-primary text-xs"
                  data-testid="demo-reset-btn"
                  style={{ background: "#0F1B2D", color: "#FFC145" }}
                >
                  <RotateCcw size={14} />
                  {resetStatus === "running" ? "Wiping…" : resetStatus === "done" ? "Wiped ✓" : "Reset for demo"}
                </button>
              </div>
            )}

            {/* Talent Cloud opt-in — recruiter-side discoverability */}
            <div className="pt-4 mt-4" style={{ borderTop: "2px dashed #E2E8F0" }}
                 data-testid="talent-cloud-section">
              <TalentCloudOptIn />
            </div>

            {/* Danger zone — GDPR self-service delete (all users) */}
            <div
              className="pt-4"
              style={{ borderTop: "3px dashed #DC2626" }}
              data-testid="danger-zone-section"
            >
              <div
                className="rm-tag mb-2"
                style={{ background: "#DC2626", borderColor: "#DC2626", color: "white" }}
              >
                Danger zone
              </div>
              <h3 className="font-display text-lg uppercase mb-2">
                Delete account & all data
              </h3>
              <p className="text-sm mb-3" style={{ color: "#475569" }}>
                Permanently erase your resume, every tailored analysis, skill
                assessments, role research, interview banks, mock interview
                transcripts, feedback, credits, and the account itself. You'll get a
                confirmation email when it's done. <strong>This cannot be undone.</strong>
              </p>
              <button
                onClick={() => setDeleteOpen(true)}
                className="rm-btn text-xs"
                style={{ background: "#DC2626", color: "white", border: "3px solid #0F1B2D" }}
                data-testid="open-delete-account-btn"
              >
                <Trash2 size={14} />
                Delete my account…
              </button>
            </div>
          </div>
        )}
      </div>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          setUser(false);
          navigate("/welcome", { replace: true });
        }}
      />
    </div>
  );
}
